import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import LoadingSpinner from '../../common/LoadingSpinner.jsx';
import { buildApiUrl } from '../../../utils/apiBase.js';
import { assertAuthApiOk } from '../../../utils/authApiErrors.js';
import { loadRecaptchaScript, getRecaptchaToken } from '../../../utils/recaptcha.js';
import { authHubAuth } from '../../../config/firebase.js';
import { useToast } from '../../../contexts/ToastContext.jsx';
import { restoreFirebaseAuthFromSession } from '../../../utils/authSession.js';
import HubAuthShell from './HubAuthShell.jsx';

const POLL_INTERVAL_MS = 3000;

const COPY = {
  title: 'メールアドレス確認のご案内',
  bodyWithWatch:
    'ご登録いただいたメールアドレス宛に、確認用のリンクをお送りしました。メール内のリンクを開いていただくと、こちらの画面で自動的にログインし、サービスのご利用を開始できます。',
  bodyWithoutWatch:
    'メール内のリンクで確認を完了したうえで、ログインページよりお進みください。確認メールが届いていない場合は、下の案内より再送をお申し込みください。',
  resendToggle: 'メールが届いていない場合は、こちらから再送のお手続きができます。',
  waiting:
    'メールアドレスの確認完了をお待ちしています。確認が完了次第、自動的にログインいたします。このまましばらくお待ちください。',
  loggingIn: '確認が完了しました。ログインしています…',
};

/**
 * メール確認待ち・再送ページ
 */
const VerifyEmailPendingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const initial = location.state || {};
  const mailSendFailed = initial.fromSignup && initial.verificationEmailSent === false;

  const [clinicName, setClinicName] = useState(initial.clinicName || initial.clinicCode || '');
  const [userId, setUserId] = useState(initial.userId || '');
  const [email, setEmail] = useState(initial.email || '');
  const [selectedClinicId, setSelectedClinicId] = useState(initial.clinicId || '');
  const [ambiguousCandidates, setAmbiguousCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(Boolean(initial.fromSignup || initial.signupMessage));
  const [infoMessage, setInfoMessage] = useState(
    initial.signupMessage
      || (initial.fromSignup
        ? (mailSendFailed
          ? 'ご登録は完了しましたが、確認メールの送信に失敗しました。お手数ですが、以下のフォームから再送をお願いいたします。'
          : '確認メールをお送りしました。メールに記載のリンクを開いていただくと、この画面で自動的にログインできます。')
        : '')
  );
  const [resendOpen, setResendOpen] = useState(mailSendFailed);
  const [watching, setWatching] = useState(null);
  const [phase, setPhase] = useState('idle');
  const [pollNonce, setPollNonce] = useState(0);
  const completingRef = useRef(false);

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  useEffect(() => {
    let cancelled = false;
    let timerId = null;
    completingRef.current = false;

    const completeLogin = async () => {
      if (completingRef.current || cancelled) return;
      completingRef.current = true;
      setPhase('logging-in');
      try {
        const response = await fetch(buildApiUrl('/api/auth/complete-email-verification-login'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.success) {
          assertAuthApiOk(response, data, 'ログインに失敗しました。');
          throw new Error(data.message || 'ログインに失敗しました。');
        }
        if (data.authCustomToken) {
          await signInWithCustomToken(authHubAuth, data.authCustomToken);
        } else {
          const restored = await restoreFirebaseAuthFromSession();
          if (!restored) {
            throw new Error('ログインに失敗しました。ログインページよりお進みください。');
          }
        }
        showToast('auth.login.success');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        completingRef.current = false;
        if (!cancelled) {
          setPhase('waiting');
          setError(err.message || 'ログインに失敗しました。ログインページよりお進みください。');
        }
      }
    };

    const scheduleNextPoll = () => {
      if (cancelled) return;
      timerId = setTimeout(pollOnce, POLL_INTERVAL_MS);
    };

    const pollOnce = async () => {
      try {
        const response = await fetch(buildApiUrl('/api/auth/email-verification-status'), {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;

        // watch なしは停止。一時障害はポーリング継続。
        if (response.status === 401 || (response.ok && data.watching === false)) {
          setWatching(false);
          setPhase('idle');
          return;
        }

        if (!response.ok) {
          scheduleNextPoll();
          return;
        }

        setWatching(true);
        if (data.verified) {
          await completeLogin();
          return;
        }
        setPhase('waiting');
        scheduleNextPoll();
      } catch {
        if (!cancelled) {
          scheduleNextPoll();
        }
      }
    };

    pollOnce();

    return () => {
      cancelled = true;
      if (timerId) clearTimeout(timerId);
    };
  }, [navigate, showToast, pollNonce]);

  const handleClinicInputChange = (value) => {
    setClinicName(value);
    setSelectedClinicId('');
    setAmbiguousCandidates([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);
    try {
      const recaptchaToken = await getRecaptchaToken('resend_email_verification');
      const payload = {
        clinicName: clinicName.trim(),
        userId: userId.trim(),
        email: email.trim(),
        recaptchaToken,
      };
      if (selectedClinicId) payload.clinicId = selectedClinicId;

      const response = await fetch(buildApiUrl('/api/auth/resend-email-verification'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (response.status === 409 && data?.error === 'clinic-ambiguous') {
        setAmbiguousCandidates(Array.isArray(data.candidates) ? data.candidates : []);
        setError(data.message || '同名のクリニックが複数見つかりました。一覧からお選びください。');
        return;
      }

      if (!response.ok) {
        assertAuthApiOk(response, data, '確認メールの再送に失敗しました。');
        throw new Error(data.message || '確認メールの再送に失敗しました。しばらくしてから再度お試しください。');
      }

      setInfoMessage(
        data.message
          || 'ご入力内容に問題がなければ、確認メールをお送りしました。しばらく経っても届かない場合は、入力内容をご確認のうえ再度お試しください。'
      );
      setSuccess(true);
      setPhase('waiting');
      setPollNonce((n) => n + 1);
    } catch (err) {
      setError(err.message || '確認メールの再送に失敗しました。しばらくしてから再度お試しください。');
    } finally {
      setLoading(false);
    }
  };

  const subtitle = watching === false
    ? COPY.bodyWithoutWatch
    : COPY.bodyWithWatch;

  return (
    <HubAuthShell>
      <div className="hub-auth-header">
        <div className="hub-auth-logo">
          <img src="/logo.webp" alt="Synapse" style={{ width: 48, height: 48, objectFit: 'contain' }} />
        </div>
        <h1 className="hub-auth-title">{COPY.title}</h1>
        <p className="hub-auth-subtitle">{subtitle}</p>
      </div>

      {(phase === 'waiting' || phase === 'logging-in') && (
        <div className="hub-auth-verify-wait" role="status">
          <LoadingSpinner size="small" />
          <p>{phase === 'logging-in' ? COPY.loggingIn : COPY.waiting}</p>
        </div>
      )}

      {success && infoMessage && (
        <div className="hub-auth-info-banner">
          {infoMessage}
        </div>
      )}

      {error && (
        <div className="hub-auth-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        className="hub-auth-resend-toggle"
        aria-expanded={resendOpen}
        onClick={() => setResendOpen((open) => !open)}
      >
        <span>{COPY.resendToggle}</span>
        <svg
          className={`hub-auth-resend-toggle-chevron${resendOpen ? ' is-open' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div className={`hub-auth-resend-collapse${resendOpen ? ' is-open' : ''}`}>
        <div className="hub-auth-resend-collapse-inner">
          <form onSubmit={handleSubmit}>
            <div className="hub-form-group">
              <label className="hub-form-label hub-form-required">クリニック名またはコード</label>
              <input
                type="text"
                className="hub-form-input"
                value={clinicName}
                onChange={(e) => handleClinicInputChange(e.target.value)}
                required={resendOpen}
                autoComplete="organization"
              />
            </div>

            {ambiguousCandidates.length > 0 && (
              <div className="hub-form-group">
                <label className="hub-form-label hub-form-required">クリニックを選択</label>
                <select
                  className="hub-form-input"
                  value={selectedClinicId}
                  onChange={(e) => setSelectedClinicId(e.target.value)}
                  required={resendOpen}
                >
                  <option value="">選択してください</option>
                  {ambiguousCandidates.map((c) => (
                    <option key={c.clinicId} value={c.clinicId}>
                      {c.name}{c.code ? ` (${c.code})` : ''}{c.address ? ` — ${c.address}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="hub-form-group">
              <label className="hub-form-label hub-form-required">ユーザーID</label>
              <input
                type="text"
                className="hub-form-input"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                required={resendOpen}
                autoComplete="username"
              />
            </div>

            <div className="hub-form-group">
              <label className="hub-form-label hub-form-required">ご確認いただくメールアドレス</label>
              <input
                type="email"
                className="hub-form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={resendOpen}
                autoComplete="email"
              />
            </div>

            <button type="submit" className="hub-btn hub-btn-primary" style={{ width: '100%' }} disabled={loading}>
              {loading ? <><LoadingSpinner size="small" /> 送信中...</> : '確認メールを再送する'}
            </button>
          </form>
        </div>
      </div>

      <div className="hub-auth-footer">
        <p><Link to="/login">ログインページに戻る</Link></p>
      </div>
    </HubAuthShell>
  );
};

export default VerifyEmailPendingPage;
